import { Link } from "react-router-dom";
import { ShieldCheck, Database, Cookie, UserCog, Mail } from "lucide-react";

export default function Privacy() {
    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="privacy-page">
            <div className="max-w-[900px] mx-auto px-6 sm:px-10">
                <div className="mb-16 fade-up">
                    <div className="label-caps text-[#8C3A3A] mb-4">Studio Policy</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A] leading-none mb-8">
                        Privacy<br /><span className="italic text-[#5A5A5A]">& your data.</span>
                    </h1>
                    <p className="text-[#5A5A5A] leading-relaxed max-w-2xl">
                        Art N Moore is a solo Australian studio run by Nicholas Moore. Because the work is intimate,
                        so is our approach to your data — we collect only what we need to fulfil your order or
                        commission, and we never share it beyond that.
                    </p>
                </div>

                <div className="space-y-16">
                    {/* What we collect */}
                    <section data-testid="privacy-collect">
                        <div className="flex items-center gap-3 mb-6">
                            <Database className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">What We Collect</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong className="text-[#1A1A1A]">Order details</strong> — name, shipping address,
                                    email, phone (if provided), and the items you ordered.</li>
                                <li><strong className="text-[#1A1A1A]">Commission inquiries</strong> — the information
                                    you enter in the form: name, email, medium, size, budget, description, and any
                                    reference image you attach.</li>
                                <li><strong className="text-[#1A1A1A]">Studio list (waitlist) sign-ups</strong> — your
                                    email address and where on the site you joined from.</li>
                                <li><strong className="text-[#1A1A1A]">Payment information</strong> — handled entirely
                                    by <strong className="text-[#1A1A1A]">Stripe</strong>. We never see, store, or have
                                    access to your card details.</li>
                            </ul>
                        </div>
                    </section>

                    {/* How we use it */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-use">
                        <div className="flex items-center gap-3 mb-6">
                            <UserCog className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">How We Use Your Information</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>Your information is used only to:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Process and fulfil your order, including sending it to our print & shipping partners.</li>
                                <li>Respond to your commission inquiry personally.</li>
                                <li>Send transactional emails (order confirmation, shipping updates, commission acknowledgements).</li>
                                <li>Send you occasional studio updates if — and only if — you joined the studio list. You can unsubscribe at any time using the link in any email.</li>
                            </ul>
                            <p>
                                We do not sell, rent, trade, or share your personal information with any third party
                                for marketing purposes.
                            </p>
                        </div>
                    </section>

                    {/* Third parties */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-third-parties">
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldCheck className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Services We Rely On</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Running the studio online requires a small number of trusted service providers. Each
                                receives only the minimum information needed to do its job:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong className="text-[#1A1A1A]">Stripe</strong> — processes all card payments
                                    securely and is PCI-DSS compliant.</li>
                                <li><strong className="text-[#1A1A1A]">Resend</strong> — sends order, commission, and
                                    studio list emails on our behalf.</li>
                                <li><strong className="text-[#1A1A1A]">Behold</strong> — embeds our public Instagram
                                    feed on the site (does not receive any customer data).</li>
                                <li><strong className="text-[#1A1A1A]">A print-on-demand fulfilment partner</strong>{" "}
                                    — receives your name, shipping address, and the print you ordered so they can
                                    print and dispatch it.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Cookies */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-cookies">
                        <div className="flex items-center gap-3 mb-6">
                            <Cookie className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Cookies & Site Data</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                We use minimal, functional browser storage — for example, to remember what is in your
                                cart between visits. We do not run any advertising or cross-site tracking cookies.
                            </p>
                            <p>
                                Stripe places its own security cookies during checkout to prevent fraud; these are
                                covered by <a href="https://stripe.com/en-au/privacy" target="_blank" rel="noreferrer" className="text-[#1A1A1A] underline hover:text-[#8C3A3A]">Stripe's privacy policy</a>.
                            </p>
                        </div>
                    </section>

                    {/* Content note */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-content">
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldCheck className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Adult Content & Consent</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Some of the artworks on this site include artistic nudity and sensual portraiture of
                                the male form. Where a work contains explicit content, it is soft-blurred by default
                                and only revealed with a deliberate click. By choosing to reveal such content, you
                                confirm you are of legal age in your jurisdiction and are viewing this work of your
                                own volition.
                            </p>
                        </div>
                    </section>

                    {/* Your rights */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-rights">
                        <div className="flex items-center gap-3 mb-6">
                            <UserCog className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Your Rights</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Under the Australian Privacy Principles — and, where applicable, GDPR for European
                                customers — you may request at any time to:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Access the personal information we hold about you.</li>
                                <li>Correct anything that is inaccurate or out of date.</li>
                                <li>Have your information deleted from our records (subject to any legal or
                                    tax-record retention obligations).</li>
                                <li>Unsubscribe from all non-transactional emails.</li>
                            </ul>
                            <p>
                                To exercise any of these rights, email us at{" "}
                                <a href="mailto:art.nickmoore@gmail.com" className="text-[#1A1A1A] underline hover:text-[#8C3A3A]">art.nickmoore@gmail.com</a>{" "}
                                and we will respond within 30 days.
                            </p>
                        </div>
                    </section>

                    {/* Contact */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="privacy-contact">
                        <div className="flex items-center gap-3 mb-6">
                            <Mail className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">Questions About Your Privacy?</h2>
                        </div>
                        <p className="text-[#5A5A5A] leading-relaxed text-base mb-6">
                            The studio inbox is monitored personally by Nicholas.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="mailto:art.nickmoore@gmail.com"
                                className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] transition-colors"
                                data-testid="privacy-email-btn"
                            >
                                <Mail className="w-4 h-4" /> art.nickmoore@gmail.com
                            </a>
                            <Link
                                to="/policies"
                                className="inline-flex items-center gap-3 border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors"
                                data-testid="privacy-shipping-link"
                            >
                                Shipping & Returns
                            </Link>
                        </div>
                    </section>
                </div>

                <div className="mt-24 pt-8 border-t border-black/[0.06] text-xs text-[#8A8A8A] tracking-widest uppercase">
                    Last updated {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
                </div>
            </div>
        </div>
    );
}
